import { BannerButton, Filler } from "../Components";
import React, { useEffect, useRef, useState } from "react";
import { IViewShareData } from ".";
import { Tooltip } from "@material-ui/core";
import { ViewShareSharing } from "./ViewShareSharing";
import "@metricell/smarttoolscore/dist/module/Styles/metricell.css";
import { format } from "date-fns";

export interface IGenerateViewProps {
    deleteLink(id: string): void;
    revertDeleteLink(id: string): void;
    getLink(): void;
    getList(event: React.SyntheticEvent);
    keyWords: string;
    tags: string[];
    link: string;
    viewShareId: string;
    viewName: string;
    setKeyWords: (e) => void;
    setTags: (e) => void;
    setViewName: (e) => void;
    setViewShare: (e) => void;
    shareLink(id: string): void;
    viewShare: IViewShareData;
    viewShareList: IViewShareData[];
}

interface IViewShare {
    viewShareId: string;
    viewName: string;
    keyWords: string;
}

type SmartSupportTabs = "generate" | "list";

export const GenerateView: React.FunctionComponent<IGenerateViewProps> = (
    props,
): React.ReactElement => {
    const [tab, setTab] = useState<SmartSupportTabs>(`generate`),
        [searchTerm, setSearchTerm] = useState<string>(``),
        inputRef = useRef<HTMLInputElement>(null),
        isValidRegex = /^[A-Za-z0-9 ]+$/,
        nameRef = useRef<HTMLInputElement>(null),
        searchRef = useRef<HTMLInputElement>(null),
        [appId, setAppId] = useState<number[]>(),
        [input, setInput] = useState(``),
        [shareLink, setShareLink] = useState<string>(``),
        [copyPopup, setCopyPopup] = useState(false),
        [deletePopup, setDeletePopup] = useState(false),
        [sharingPopup, setSharingPopup] = useState(false),
        [isKeyReleased, setIsKeyReleased] = useState(false),
        sharingRef = useRef<HTMLInputElement>(null);

    const copyOnClick = (): void => {
        const condition = props.link === ``;

        if (!condition && inputRef.current) {
            // Set the input element as the selected text.
            inputRef.current.select();
            // Do the same but on mobile browsers.
            inputRef.current.setSelectionRange(0, 99999);

            // Ask the browser to copy whatever text is selected.
            document.execCommand(`copy`);
            setCopyPopup(true);
            setTimeout(() => {
                setCopyPopup(false);
            }, 6000);
        }
    };

    useEffect(() => {
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event): void {
            if (
                sharingRef.current &&
                !sharingRef.current.contains(event.target)
            ) {
                setSharingPopup(false);
            }
        }
        // Bind the event listener
        document.addEventListener(`mousedown`, handleClickOutside);
        return (): void => {
            // Unbind the event listener on clean up
            document.removeEventListener(`mousedown`, handleClickOutside);
        };
    }, [sharingRef]);

    const DeleteOnClick = (): void => {
        setDeletePopup(true);
        setTimeout(() => {
            setDeletePopup(false);
        }, 6000);
    };

    const getLink = async (id: string): Promise<void> => {
        const resultText = await $.ajax({
            data: {
                viewShareId: id,
            },
            method: `GET`,
            url: `/ViewShare/GetViewShareLink`,
        });

        const resultObj: IViewShare = JSON.parse(resultText);

        setShareLink(
            `${window.location.origin}/ViewShare-${resultObj.viewShareId}`,
        );
    };

    const handleSupportClick = (): void => {
        setTab(`generate`);
    };

    const handleRequestClick = (): void => {
        setTab(`list`);
    };

    const filterByApp = (newAppId: number): void => {
        const activeIds = new Array<number>();

        appId?.forEach((id) => activeIds.push(id));
        if (activeIds?.includes(newAppId)) {
            activeIds.forEach((value, index) => {
                if (value === newAppId) {
                    activeIds.splice(index, 1);
                }
            });
        } else {
            activeIds.push(newAppId);
        }
        setAppId(activeIds);
    };

    const checkViewNameExists = (): boolean => {
        if (props.viewName === `` || props.viewName === null) {
            return true;
        }
        return false;
    };

    const generateViewShare = (): void => {
        const trimmedInput = input.trim();

        if (trimmedInput !== ``) {
            props.setTags((prevState) => [...prevState, trimmedInput]);
            setInput(``);
        }
        props.getLink();
    };

    // Keyword input methods
    const onChange = (e): void => {
        const { value } = e.target;

        if (isValidRegex.test(value) || value === ``) {
            setInput(value);
        }
    };

    const onKeyDown = (e): void => {
        const { key } = e;

        const trimmedInput = input.trim();

        if (
            key === `,` &&
            trimmedInput.length &&
            !props.tags?.includes(trimmedInput)
        ) {
            e.preventDefault();
            props.setTags((prevState) => [...prevState, trimmedInput]);
            setInput(``);
        }

        if (
            key === `Backspace` &&
            !input.length &&
            props.tags.length &&
            isKeyReleased
        ) {
            const tagsCopy = [...props.tags];

            const poppedTag = tagsCopy.pop();

            e.preventDefault();
            props.setTags(tagsCopy);
            setInput(poppedTag);
        }

        setIsKeyReleased(false);
    };

    const onKeyUp = (): void => {
        setIsKeyReleased(true);
    };

    const deleteTag = (index): void => {
        props.setTags((prevState) => prevState.filter((tag, i) => i !== index));
    };

    return (
        <div className={`viewshare ${tab === `list` ? ` wider-tab` : ``}`}>
            <div className={`viewshare-banner`}>
                <div className={`support-banner`}>
                    <BannerButton
                        isActive={tab === `generate`}
                        onClick={handleSupportClick}
                        title={`Generate a View`}
                    />
                    <BannerButton
                        isActive={tab === `list`}
                        onClick={handleRequestClick}
                        title={`View Library`}
                    />
                </div>
                {tab === `list` && (
                    <div className={`viewshare-library-search-container`}>
                        <input
                            onChange={(e): void => {
                                const { value } = e.target;

                                if (isValidRegex.test(value) || value === ``) {
                                    setSearchTerm(e.target.value);
                                }
                            }}
                            placeholder={`Search`}
                            ref={searchRef}
                            type={`text`}
                            value={searchTerm}
                        />
                        <img
                            className={`viewshare-icon`}
                            src={`/Images/UI/search.svg`}
                        />
                    </div>
                )}
            </div>
            <div className={`generateview`}>
                {tab === `generate` && (
                    <React.Fragment>
                        <p className={`generateview-para`}>
                            Share what&apos;s loaded on your screen with a
                            colleague.
                        </p>
                        <p className={`generateview-para`}>
                            Gone are the days where you have to screenshot your
                            SmartTools: just load what you want to share, and
                            click on the &apos;Generate&apos; button below.
                        </p>
                        <p className={`generateview-para`}>
                            Anyone with SmartTools credentials and the correct
                            data permissions can log in and interact with the
                            view!
                        </p>
                        <div className={`generateview-linkcontainer`}>
                            <div className={`tag-container`}>
                                <input
                                    maxLength={50}
                                    onChange={(e): void => {
                                        const { value } = e.target;

                                        if (
                                            isValidRegex.test(value) ||
                                            value === ``
                                        ) {
                                            props.setViewName(e.target.value);
                                        }
                                    }}
                                    placeholder={`View Name`}
                                    ref={nameRef}
                                    type={`text`}
                                    value={props.viewName}
                                />
                            </div>
                            <div className={`scroll`} style={{ maxHeight: 75 }}>
                                <div className={`tag-container`}>
                                    {props.tags.map((tag, index) => (
                                        <div className={`tag`} key={index}>
                                            <span>{tag}</span>
                                            <button
                                                onClick={(): void =>
                                                    deleteTag(index)
                                                }
                                            >
                                                x
                                            </button>
                                        </div>
                                    ))}
                                    <div className={`tag-input-image`}>
                                        <input
                                            disabled={props.tags.length > 4}
                                            maxLength={30}
                                            onChange={(e): void => onChange(e)}
                                            onKeyDown={onKeyDown}
                                            onKeyUp={onKeyUp}
                                            placeholder={
                                                props.tags.length < 1
                                                    ? `Keyword (Optional)`
                                                    : ``
                                            }
                                            value={input}
                                        />
                                        <Tooltip
                                            title={`Enter comma separated keywords`}
                                        >
                                            <img
                                                alt={`info`}
                                                className={`viewshare-icon`}
                                                src={`/Images/InfoIcon.svg`}
                                            />
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Filler />
                        {props.link !== `` && (
                            <div
                                className={`tag-container`}
                                onClick={copyOnClick}
                            >
                                <input
                                    disabled={props.link === ``}
                                    placeholder={`View Link`}
                                    readOnly={true}
                                    ref={inputRef}
                                    type={`text`}
                                    value={props.link}
                                />
                                <img
                                    alt={`copy`}
                                    className={`viewshare-icon`}
                                    src={`/Images/CopyIcon.svg`}
                                />
                            </div>
                        )}
                        <Filler />
                        <div className={`generateview-button-container`}>
                            <button
                                className={`generateview-generatebutton`}
                                disabled={checkViewNameExists()}
                                onClick={(): void => {
                                    generateViewShare();
                                }}
                            >
                                Generate
                            </button>
                        </div>
                    </React.Fragment>
                )}
                {tab === `list` && (
                    <React.Fragment>
                        <div className={`viewshare-library-container`}>
                            <div className={`viewshare-folder-filters`}>
                                {props.viewShareList
                                    ?.filter(
                                        (thing, i, arr) =>
                                            arr.findIndex(
                                                (t) =>
                                                    t.moduleId ===
                                                    thing.moduleId,
                                            ) === i,
                                    )
                                    .filter((v) =>
                                        v.applicationName
                                            .toUpperCase()
                                            .includes(searchTerm.toUpperCase()),
                                    )
                                    .map((link) => (
                                        <div
                                            className={`viewshare-folder ${
                                                appId?.includes(link.moduleId)
                                                    ? ` viewshare-selected`
                                                    : ``
                                            }`}
                                            key={link.moduleId}
                                            onClick={(e): void =>
                                                filterByApp(link.moduleId)
                                            }
                                        >
                                            <img
                                                className={`viewshare-icon`}
                                                src={`/Images/FolderIcon.svg`}
                                            />
                                            <span>{link.applicationName}</span>
                                        </div>
                                    ))}
                            </div>
                            <div
                                className={`scroll`}
                                style={{
                                    minHeight: 30,
                                    width: `65%`,
                                }}
                            >
                                <div className={`viewshare-library`}>
                                    {props.viewShareList
                                        ?.filter(
                                            (v) =>
                                                v.keyWords
                                                    .toUpperCase()
                                                    .includes(
                                                        searchTerm.toUpperCase(),
                                                    ) ||
                                                v.viewShareName
                                                    .toUpperCase()
                                                    .includes(
                                                        searchTerm.toUpperCase(),
                                                    ) ||
                                                v.applicationName
                                                    .toUpperCase()
                                                    .includes(
                                                        searchTerm.toUpperCase(),
                                                    ),
                                        )
                                        ?.filter(
                                            (vs) =>
                                                appId?.includes(vs.moduleId) ||
                                                appId === null ||
                                                appId === undefined ||
                                                appId?.length < 1,
                                        )
                                        ?.map((link) => (
                                            <div
                                                className={`viewshare-library-listing-container ${
                                                    props?.viewShare?.id?.includes(
                                                        link.id,
                                                    )
                                                        ? ` viewshare-selected`
                                                        : ``
                                                }`}
                                                key={link?.id}
                                                onClick={(e): void =>
                                                    props.setViewShare(link)
                                                }
                                            >
                                                <div
                                                    className={`viewshare-library-listing`}
                                                >
                                                    <div
                                                        className={`listing-actions`}
                                                    >
                                                        <div
                                                            className={`viewshare-icon`}
                                                        >
                                                            <img
                                                                onClick={(): void => {
                                                                    void props.deleteLink(
                                                                        link.id,
                                                                    );
                                                                    DeleteOnClick();
                                                                }}
                                                                src={`/Images/UI/Icons_Delete.svg`}
                                                            />
                                                        </div>
                                                        <div
                                                            className={`viewshare-icon`}
                                                            onClick={(
                                                                e,
                                                            ): void => {
                                                                void getLink(
                                                                    link.id,
                                                                );
                                                                setSharingPopup(
                                                                    true,
                                                                );
                                                            }}
                                                        >
                                                            <img
                                                                src={`Images/UI/Icon_arrowcurve.svg`}
                                                            />
                                                        </div>
                                                        {sharingPopup &&
                                                            link.id ===
                                                                props.viewShare
                                                                    .id && (
                                                                <div
                                                                    ref={
                                                                        sharingRef
                                                                    }
                                                                >
                                                                    <ViewShareSharing
                                                                        link={
                                                                            shareLink
                                                                        }
                                                                        name={
                                                                            props.viewName
                                                                        }
                                                                    />
                                                                </div>
                                                            )}
                                                    </div>
                                                    <div
                                                        onClick={(e): void =>
                                                            props.setViewShare(
                                                                link,
                                                            )
                                                        }
                                                    >
                                                        <small>
                                                            {
                                                                link.applicationName
                                                            }
                                                        </small>
                                                        <div
                                                            className={`listing-title`}
                                                        >
                                                            {link.viewShareName}{" "}
                                                            -{" "}
                                                            {format(
                                                                new Date(
                                                                    link.creationUtcTime +
                                                                        "Z",
                                                                ),
                                                                `dd/MM/yyyy hh:mm:ss`,
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                        <div className={`scroll`} style={{ maxHeight: 85 }}>
                            {props.viewShare &&
                                props.viewShareList?.includes(
                                    props.viewShare,
                                ) && (
                                    <div className={`viewshare-entry`}>
                                        <div
                                            className={`viewshare-entry-title`}
                                        >
                                            <h4>
                                                {props.viewShare?.viewShareName}{" "}
                                                -{" "}
                                                {format(
                                                    new Date(
                                                        props.viewShare
                                                            ?.creationUtcTime +
                                                            "Z",
                                                    ),
                                                    `dd/MM/yyyy hh:mm:ss`,
                                                )}
                                            </h4>
                                        </div>
                                        <div
                                            className={`viewshare-entry-details`}
                                        >
                                            <div>
                                                <p
                                                    className={`viewshare-entry-keywords`}
                                                >
                                                    Keywords:{" "}
                                                    {props.viewShare
                                                        ?.keyWords &&
                                                    props.viewShare
                                                        ?.keyWords === ``
                                                        ? `None`
                                                        : props.viewShare
                                                              ?.keyWords}
                                                </p>
                                                <p
                                                    className={`viewshare-entry-author`}
                                                >
                                                    Author:{" "}
                                                    {props.viewShare?.author}
                                                </p>
                                            </div>
                                            <button
                                                className={`viewshare-entry-open`}
                                                onClick={(): void =>
                                                    void props.shareLink(
                                                        props.viewShare.id,
                                                    )
                                                }
                                            >
                                                Open
                                            </button>
                                        </div>
                                    </div>
                                )}
                        </div>
                    </React.Fragment>
                )}
                {copyPopup && (
                    <div className={`viewshare-popup`}>
                        <div className={`viewshare-image viewshare-copy`}>
                            <img alt={`copy`} src={`/Images/CopyIcon.svg`} />
                        </div>
                        <p>Link Copied to Clipboard</p>
                    </div>
                )}
                {deletePopup && (
                    <div className={`viewshare-popup viewshare-popup-delete`}>
                        <div>
                            <div className={`viewshare-image`}>
                                <img
                                    alt={`close`}
                                    onClick={(): void => {
                                        setDeletePopup(false);
                                    }}
                                    src={`/Images/UI/Icons_Delete.svg`}
                                />
                            </div>
                            <p>{props.viewShare.viewShareName} Link Deleted</p>
                        </div>
                        <button
                            onClick={(): void => {
                                void props.revertDeleteLink(props.viewShare.id);
                            }}
                        >
                            Undo
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
